import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CurrencyContext = createContext();

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState({
    code: 'USD',
    symbol: '$',
    name: 'US Dollar',
    rate: 1.0,
    country_code: 'US'
  });
  
  const [isLoading, setIsLoading] = useState(true);

  // Auto-detect currency on mount
  useEffect(() => {
    detectCurrency();
  }, []);

  const detectCurrency = async () => {
    try {
      // Check if user has manually set a currency preference first (avoid API call)
      const savedCurrency = localStorage.getItem('userCurrency');
      if (savedCurrency) {
        const parsed = JSON.parse(savedCurrency);
        
        // If user manually set currency OR we detected within last 24 hours, use cached
        const cacheAge = Date.now() - (parsed.timestamp || 0);
        const oneDayInMs = 24 * 60 * 60 * 1000;
        
        if (parsed.detected_by === 'manual' || cacheAge < oneDayInMs) {
          console.log('Using cached currency:', parsed.code);
          setCurrencyState(parsed);
          setIsLoading(false);
          return;
        }
      }
      
      // Only call API if no cache or cache is old
      const response = await axios.get(`${API}/currency/detect`, {
        timeout: 3000 // 3 second timeout
      });
      const detectedCurrency = response.data;
      
      // Add timestamp for cache validation
      detectedCurrency.timestamp = Date.now();
      
      // Use auto-detected currency
      setCurrencyState(detectedCurrency);
      localStorage.setItem('userCurrency', JSON.stringify(detectedCurrency));
      
      console.log('Currency auto-detected:', detectedCurrency);
    } catch (error) {
      console.error('Error detecting currency:', error);
      
      // Check localStorage as fallback
      const savedCurrency = localStorage.getItem('userCurrency');
      if (savedCurrency) {
        const parsed = JSON.parse(savedCurrency);
        setCurrencyState(parsed);
      } else {
        // Final fallback to USD
        const fallback = {
          code: 'USD',
          symbol: '$',
          name: 'US Dollar',
          rate: 1.0,
          detected_by: 'fallback',
          timestamp: Date.now()
        };
        setCurrencyState(fallback);
        localStorage.setItem('userCurrency', JSON.stringify(fallback));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const changeCurrency = async (currencyCode) => {
    try {
      // Get currency details from API
      const listResponse = await axios.get(`${API}/currency/list`);
      const currencies = listResponse.data.currencies;
      const newCurrency = currencies[currencyCode];

      if (!newCurrency) {
        console.error('Invalid currency code:', currencyCode);
        return;
      }

      // Update state
      const updatedCurrency = {
        code: currencyCode,
        symbol: newCurrency.symbol,
        name: newCurrency.name,
        rate: newCurrency.rate,
        detected_by: 'manual'
      };

      setCurrencyState(updatedCurrency);
      localStorage.setItem('userCurrency', JSON.stringify(updatedCurrency));

      // If user is logged in, update backend
      try {
        await axios.post(`${API}/currency/set?currency_code=${currencyCode}`, {}, {
          withCredentials: true
        });
      } catch (err) {
        // User might not be logged in, that's okay
        console.log('Not logged in, currency saved locally only');
      }

      console.log('Currency changed to:', currencyCode);
    } catch (error) {
      console.error('Error changing currency:', error);
    }
  };

  const convertPrice = (priceUSD) => {
    return priceUSD * currency.rate;
  };

  const formatPrice = (priceUSD) => {
    const converted = convertPrice(priceUSD);
    const symbol = currency.symbol;
    const formatted = converted.toFixed(2);

    // For text-based currency codes (SAR, AED, PKR), add space
    // For symbol currencies ($, £, €, ₹), no space
    if (currency.code === 'SAR' || currency.code === 'AED' || currency.code === 'PKR') {
      return `${symbol} ${formatted}`;  // "SAR 108.75" or "PKR 27550.00"
    } else {
      return `${symbol}${formatted}`;  // "$29.00"
    }
  };

  const value = {
    currency,
    setCurrency: changeCurrency,
    convertPrice,
    formatPrice,
    isLoading
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};
